import { useGetPostApplyRelevantJobsQuery } from './__generated__/findPostApplyRelevantJobs.gqlgenerated';
import { tryConsumePostApplyAdPending } from './postApplyAdPending';
import {
    JobCard,
    type JobCardData,
    type DislikedJobCardProps,
} from '../../../../../jobcard/components/JobCard/JobCard';
import { DislikedJobsProvider } from '../../../../../jobcard/context/DislikedJobsContext';
import { Box, Button, Heading, Pressable, type TextRef } from '../../../../../mads';
import { ShimmerPlaceHolderWithGradient as ShimmerPlaceHolder } from '../../../../../shared/components/ShimmerPlaceholder';
import { useI18n } from '../../../../../shared/I18n';
import {
    JSTLandingScreen,
    type JSTLandingScreen as JSTLandingScreenType,
    JSTViewJobTarget,
} from '../../../../../shared/native-modules/tare/JSTLandingScreen';
import navigateTo from '../../../../../shared/native-modules/tare/navigateTo';
import eventEmitter from '../../../../../shared/util/eventEmitter';
import { prepareJobClickUrl, withFromParam } from '../../../../../shared/util/urlUtils';
import { useViewjobProctorAllocation } from '../../../../proctor/__generated__/ViewjobProctorContext';
import { JsjPostApplyAdTst } from '../../../../proctor/__generated__/ViewjobProctorSpecifications';
import { useViewjobOneGraphModel } from '../../../context/ViewJobDataContext';
import { useIsDesktop } from '../../../context/ViewJobScreenDisplay';
import { CloseButton } from '../JobComponent/CloseButton/CloseButton';

import { borderRadius, colors, spacing } from '@indeed/ifl-tokens/react-native';
import { log as logPostApplyAdClicked } from '@indeed/signals-schema/com/indeed/signals/schema/jobcards/PostApplyAdClicked';
import { log as logPostApplyAdDismissed } from '@indeed/signals-schema/com/indeed/signals/schema/jobcards/PostApplyAdDismissed';
import { log as logPostApplyAdShown } from '@indeed/signals-schema/com/indeed/signals/schema/jobcards/PostApplyAdShown';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AccessibilityInfo,
    findNodeHandle,
    KeyboardAvoidingView,
    Modal,
    Platform,
    StyleSheet,
    useWindowDimensions,
} from 'react-native';

const CARD_MAX_WIDTH = 400;
const SKELETON_TITLE_HEIGHT = 36;
const SKELETON_BODY_HEIGHT = 120;
// Minimum duration the tab must have been hidden before a return is treated
// as a real 3P-apply return (filters Cmd-Tab / quick app-switch toggles).
const MIN_HIDDEN_DURATION_MS = 1500;
// Wait this long after the tab becomes visible again before showing the ad,
// so the modal doesn't pop the instant the user's eyes land on the page.
const TRIGGER_DELAY_MS = 500;

type ClickTarget = 'viewJobCta' | 'jobCardBody';
type DismissReason = 'closeButton' | 'backdrop';

// The PostApplyAd modal removes the dislike affordance (RN-4601), so the
// disliked-card swap is unreachable here. JobCard still requires the prop,
// so pass a render-nothing component.
const NoopDislikedCard: React.ComponentType<DislikedJobCardProps> = () => null;

// Avro-optional string fields are encoded as `{ string: '<value>' }` wrapper
// objects when present, or null when absent. Matches the homepage precedent
// at apps/homepage-strict/components/JobFeedMoreJobsButton/JobFeedMoreJobsButton.tsx.
const wrapOptionalString = (value: string | null | undefined) =>
    value ? { string: value } : null;

const POST_APPLY_AD_FROM = 'rjp_mob.postApplyAd';


export function PostApplyAd() {
    const i18n = useI18n();
    const isDesktop = useIsDesktop();
    const { width: screenWidth } = useWindowDimensions();
    // Overlay paddingHorizontal s3 (~12) on each side + card padding s4 (~16) on each side.
    const skeletonWidth = Math.min(screenWidth, CARD_MAX_WIDTH) - 56;
    const bucket = useViewjobProctorAllocation(JsjPostApplyAdTst).value;
    const isActive = bucket === JsjPostApplyAdTst.buckets.Active;
    // QA-only group (RN-3245): renders the ad like Active but forces the
    // iOSWidget context, the only context configured to return results in QA
    // today. Never enabled in production.
    const isTestingOnly = bucket === JsjPostApplyAdTst.buckets.Testingonly;
    const isEnabled = isActive || isTestingOnly;
    const titleRef = useRef<TextRef>(null);
    const pendingNavigationRef = useRef<JSTLandingScreenType | null>(null);

    const viewjobOneGraphModel = useViewjobOneGraphModel();
    const isLoggedIn = viewjobOneGraphModel?.jobSeeker?.loggedIn ?? false;

    const [dismissed, setDismissed] = useState(false);
    // Stay hidden until a trigger fires: on web, a tab-return from 3P apply;
    // on native, an externalApplyReturn event when the WebView modal closes.
    const [triggered, setTriggered] = useState(false);
    // Web-only counter, bumped on each tryFire so a re-trigger after a
    // dismiss causes the Shown-logging effect to re-evaluate even when
    // `triggered` was already true.
    const [showSession, setShowSession] = useState(0);

    // context / placementId resolve from the query document defaults
    // (both "indeed-postapply"). JRS forms surfaceId = "mob.<context>", which
    // must match the SurfaceId registered in JMP-11420 (mob.indeed-postapply)
    // for match providers to resolve. The testingOnly QA group overrides
    // context with iOSWidget, the only context returning results in QA today.
    const { data, loading, error } = useGetPostApplyRelevantJobsQuery({
        variables: {
            isLoggedIn,
            ...(isTestingOnly ? { context: 'iOSWidget' } : {}),
        },
        skip: !isEnabled || dismissed || !triggered,
    });

    // The job the user is currently viewing / just applied to
    // We don't want to recommend this same job back to them
    const currentJobKey = viewjobOneGraphModel?.job?.jobKey;

    // findRelevantJobs has no server-side exclude (FindRelevantJobsInput exposes
    // no excludeJobKeys field), so filter client-side: pick the first returned
    // job whose key differs from the current one. We over-request (limit: 2) so an
    // eligible fallback is already in hand and the modal never flashes-then-closes.
    // Scanning all results (not just [0]/[1]) keeps this correct if limit is raised.
    const job =
        data?.findRelevantJobs?.results?.find(
            (result) => result.job.key !== currentJobKey,
        )?.job ?? null;
    const shouldAutoDismiss =
        !loading && (error !== undefined || (data !== undefined && job === null));

    const trackingKeys = useMemo(
        () => ({
            mobvjtk: wrapOptionalString(viewjobOneGraphModel?.mobvjtk),
            feedtk: wrapOptionalString(data?.findRelevantJobs?.trackingKey ?? null),
            ctk: wrapOptionalString(viewjobOneGraphModel?.ctk),
        }),
        [
            viewjobOneGraphModel?.mobvjtk,
            viewjobOneGraphModel?.ctk,
            data?.findRelevantJobs?.trackingKey,
        ],
    );

    const dismiss = useCallback(() => setDismissed(true), []);

    // Fire postApplyAdShown exactly once per modal lifetime, on the render
    // after `job` becomes non-null. The ref guarantees once-per-mount even
    // if trackingKeys recomputes (e.g. when the query refetches). Cleared
    // by tryFire on each new web trigger so a second 3P apply re-fires Shown.
    const hasLoggedShownRef = useRef(false);

    // Web only: listen for tab-return after a 3P apply navigation. When the
    // user returns from the 3P apply tab (or restores from bfcache) AND the
    // pending flag is set (meaning a 3P apply was initiated on this tab),
    // clear any prior dismissed state and trigger the modal. Control bucket
    // attaches nothing because the effect early-returns on !isEnabled.
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (!isEnabled) return;
        if (typeof document === 'undefined') return;

        let hiddenAt: number | null = null;
        let triggerTimeoutId: ReturnType<typeof setTimeout> | null = null;

        const tryFire = () => {
            const pending = tryConsumePostApplyAdPending();
            if (!pending) return;
            // Reset the Shown-once guard and bump the session counter so the
            // logging effect re-evaluates on a re-trigger within the same
            // VJ mount.
            hasLoggedShownRef.current = false;
            setDismissed(false);
            setTriggered(true);
            setShowSession((s) => s + 1);
        };

        const scheduleTryFire = () => {
            if (triggerTimeoutId != null) clearTimeout(triggerTimeoutId);
            triggerTimeoutId = setTimeout(() => {
                triggerTimeoutId = null;
                tryFire();
            }, TRIGGER_DELAY_MS);
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                hiddenAt = Date.now();
                return;
            }
            if (document.visibilityState === 'visible') {
                const elapsed = hiddenAt != null ? Date.now() - hiddenAt : Infinity;
                hiddenAt = null;
                if (elapsed >= MIN_HIDDEN_DURATION_MS) scheduleTryFire();
            }
        };

        // iOS Safari restores the prior page from bfcache on back/forward
        // navigation without firing visibilitychange. Treat that as an
        // immediate return.
        const onPageshow = (event: PageTransitionEvent) => {
            if (event.persisted) scheduleTryFire();
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pageshow', onPageshow);

        return () => {
            if (triggerTimeoutId != null) clearTimeout(triggerTimeoutId);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pageshow', onPageshow);
        };
    }, [isEnabled]);

    // Native only: subscribe to the externalApplyReturn event, which fires when the
    // 3P external apply WebView modal closes. This is the trigger for showing the ad.
    useEffect(() => {
        if (Platform.OS === 'web') return;
        if (!isEnabled) return;

        const handler = ({ mobvjtk }: { jobKey: string; mobvjtk: string }) => {
            // Ignore externalApplyReturn emitted for a different ViewJob context
            // (the emitter is a global singleton shared across mounted VJ instances).
            if (mobvjtk !== viewjobOneGraphModel?.mobvjtk) return;
            // Re-entrancy: if the modal is already visible, ignore the event entirely
            // so we don't reset the shown-once guard or bump the session counter,
            // which would re-fire postApplyAdShown.
            if (triggered && !dismissed) return;

            setTriggered(true);
            setDismissed(false);
            hasLoggedShownRef.current = false;
            setShowSession((s) => s + 1);
        };

        eventEmitter.on('externalApplyReturn', handler);
        return () => {
            eventEmitter.off('externalApplyReturn', handler);
        };
    }, [isEnabled, dismissed, triggered, viewjobOneGraphModel?.mobvjtk]);

    const dismissWithReason = useCallback(
        (reason: DismissReason) => {
            logPostApplyAdDismissed({
                jobKey: wrapOptionalString(job?.key ?? null),
                dismissReason: reason,
                ...trackingKeys,
            });
            dismiss();
        },
        [job?.key, trackingKeys, dismiss],
    );

    // Silently dismiss when the query yields no usable result.
    useEffect(() => {
        if (shouldAutoDismiss && !dismissed) {
            setDismissed(true);
        }
    }, [shouldAutoDismiss, dismissed]);

    useEffect(() => {
        if (hasLoggedShownRef.current) return;
        if (!triggered) return;
        if (loading || !job) return;
        hasLoggedShownRef.current = true;
        logPostApplyAdShown({
            jobKey: job.key,
            ...trackingKeys,
        });
    }, [loading, job, trackingKeys, showSession, triggered]);

    const showModal = triggered && !dismissed && (job !== null || loading);

    // Web only: synthesized jobClick URL the underlying JobCard / View Details
    // anchor uses for in-tab navigation (TypedNativeModules.RNJSTNavigator is
    // undefined in the web bundle, so navigateTo silently no-ops). Same shape
    // CompanyConquestingJobCards uses via the `webTargetUrl` prop. Carries
    // `vjs=3` (via prepareJobClickUrl) and `from=POST_APPLY_AD_FROM` so
    // jobclick-daemon attributes the click correctly.
    const jobClickWebUrl = (() => {
        if (Platform.OS !== 'web') return undefined;
        const trackingUrl = job?.tracking?.jobClick?.url;
        if (!trackingUrl) return undefined;
        const prepared = prepareJobClickUrl(trackingUrl);
        return prepared ? withFromParam(prepared, POST_APPLY_AD_FROM) : undefined;
    })();

    // Web only: lock body scroll while the modal is up so the underlying VJ
    // doesn't scroll behind the overlay. RN's Modal renders into the same
    // document on web and does not lock the body the way the iOS UIModal
    // presentation does. Native is unaffected.
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (!showModal) return;
        if (typeof document === 'undefined') return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [showModal]);

    // Move screen reader focus onto the modal heading once the dialog is on
    // screen, so VO/TalkBack announce the dialog context instead of staying
    // on the previously focused control.
    useEffect(() => {
        if (!showModal) return;
        const timeout = setTimeout(() => {
            const node = titleRef.current && findNodeHandle(titleRef.current);
            if (node) {
                AccessibilityInfo.setAccessibilityFocus(node);
            }
        }, 100);
        return () => clearTimeout(timeout);
    }, [showModal]);

    // Don't render the Modal at all when the test bucket is inactive. Once
    // we mount it, we keep it mounted across dismissal so iOS can fire
    // onDismiss after the underlying UIViewController is fully torn down —
    // unmounting too early cancels the callback and we lose the chance to
    // navigate cleanly. (See handleModalDismissed below.)
    if (!isEnabled) {
        return null;
    }

    const runPendingNavigation = () => {
        const pending = pendingNavigationRef.current;
        if (pending) {
            pendingNavigationRef.current = null;
            navigateTo(pending, POST_APPLY_AD_FROM).catch((err) =>
                console.error('Failed to native navigate to View Job: ', err),
            );
        }
    };

    const handleNavigateToViewJob = (
        targetJob: JobCardData,
        clickTarget: ClickTarget,
    ) => {
        if (!targetJob.tracking) return;

        logPostApplyAdClicked({
            jobKey: targetJob.key,
            clickTarget,
            ...trackingKeys,
        });

        // Two query params must be on the URL for jobclick-daemon
        //   - `vjs=3` — 302s 3P-apply clicks to /m/viewjob
        //   - `from=<POST_APPLY_AD_FROM>` — populates click.getFrom() so
        //     the synthesized URL carries `from=<POST_APPLY_AD_FROM>`
        const preparedJobClickUrl = prepareJobClickUrl(targetJob.tracking?.jobClick?.url);
        const jobClickUrl = preparedJobClickUrl
            ? withFromParam(preparedJobClickUrl, POST_APPLY_AD_FROM)
            : undefined;
        const target = jobClickUrl
            ? JSTViewJobTarget.url(jobClickUrl)
            : JSTViewJobTarget.jobKey(targetJob.key);
        const landing = JSTLandingScreen.viewJob(target);

        // iOS: the RN Modal is presented over the nav controller, so push
        // nav fails until iOS finishes dismissing the modal (otherwise
        // JSMAppNavigator.getNavigationController asserts). Stash the
        // target and chain the navigateTo call from Modal.onDismiss, which
        // fires only after the UIViewController is fully gone.
        // Android: Modal doesn't block native push the same way, so dispatch
        // navigation directly.
        if (Platform.OS === 'ios') {
            pendingNavigationRef.current = landing;
            dismiss();
        } else {
            navigateTo(landing, POST_APPLY_AD_FROM).catch((err) =>
                console.error('Failed to native navigate to View Job: ', err),
            );
            dismiss();
        }
    };

    return (
        <Modal
            visible={showModal}
            transparent
            animationType="none"
            onRequestClose={() => dismissWithReason('backdrop')}
            onDismiss={runPendingNavigation}
            statusBarTranslucent
        >
            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                style={styles.overlay}
            >
                <Pressable
                    testID="post-apply-ad-backdrop"
                    sx={StyleSheet.absoluteFill}
                    onPress={() => dismissWithReason('backdrop')}
                    accessible={false}
                    importantForAccessibility="no"
                />
                <Box sx={styles.card} accessibilityViewIsModal>
                    <DislikedJobsProvider>
                        <Box sx={styles.headerRow}>
                            <Heading
                                ref={titleRef}
                                level={4}
                                sx={StyleSheet.flatten([
                                    styles.headerTitle,
                                    !isDesktop && styles.headerTitleCentered,
                                ])}
                                accessible
                            >
                                {i18n.gettext('You might also like')}
                            </Heading>
                            {/* Pinned top-right and out of flow so the mobile
                                title can center across the full card width. */}
                            <Box sx={styles.closeButtonContainer}>
                                <CloseButton
                                    onPress={() => dismissWithReason('closeButton')}
                                    accessibilityLabel={i18n.gettext('Close')}
                                />
                            </Box>
                        </Box>

                        <Box sx={styles.jobCardSlot}>
                            {loading ? (
                                <Box sx={styles.skeletonGroup}>
                                    <ShimmerPlaceHolder
                                        width={skeletonWidth}
                                        height={SKELETON_TITLE_HEIGHT}
                                        style={styles.skeletonBar}
                                    />
                                    <ShimmerPlaceHolder
                                        width={skeletonWidth}
                                        height={SKELETON_BODY_HEIGHT}
                                        style={styles.skeletonBar}
                                    />
                                </Box>
                            ) : job ? (
                                <JobCard
                                    data={job}
                                    viewJobFrom="postApplyAd"
                                    dislikeJobPageId="postApplyAd"
                                    containerStyle={styles.jobCard}
                                    webTargetUrl={jobClickWebUrl}
                                    loggedIn
                                    hideActionButtons
                                    DislikedJobCard={NoopDislikedCard}
                                    onPressJobCard={(jobToView) =>
                                        handleNavigateToViewJob(jobToView, 'jobCardBody')
                                    }
                                />
                            ) : null}
                        </Box>

                        <Box sx={[styles.viewJobCta, isDesktop && styles.viewJobCtaDesktop]}>
                            <Button
                                variant="primary"
                                size="lg"
                                href={jobClickWebUrl}
                                hrefAttrs={jobClickWebUrl ? { target: '_self' } : undefined}
                                onPress={() => job && handleNavigateToViewJob(job, 'viewJobCta')}
                                disabled={!job || loading}
                            >
                                {i18n.gettext('View Details')}
                            </Button>
                        </Box>
                    </DislikedJobsProvider>
                </Box>
            </KeyboardAvoidingView>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: colors.utilityOverlay,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: spacing.s3,
    },
    card: {
        backgroundColor: colors.background,
        borderRadius: borderRadius.lg,
        width: '100%',
        maxWidth: 400,
        maxHeight: '90%',
        padding: spacing.s4,
        overflow: 'hidden',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: spacing.s3,
    },
    headerTitle: {
        color: colors.foregroundStrong,
        // Fill the row so the close button (absolute) doesn't shrink it;
        // textAlign then controls centered (mobile) vs left (desktop).
        flex: 1,
    },
    headerTitleCentered: {
        textAlign: 'center',
    },
    closeButtonContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        justifyContent: 'center',
    },
    jobCardSlot: {
        marginBottom: spacing.s3,
    },
    skeletonGroup: {
        gap: spacing.s2,
    },
    skeletonBar: {
        borderRadius: borderRadius.md,
    },
    jobCard: {
        width: '100%',
    },
    viewJobCta: {
        marginTop: spacing.s2,
    },
    // Desktop: right-align the CTA so it sizes to its content (auto-width)
    // instead of stretching full-width. The row container's main axis is
    // horizontal, so the Button no longer stretches; `flex-end` pins it right.
    viewJobCtaDesktop: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
});
